import { supabase } from '../lib/supabase.js';

export class ReferralManager {
  constructor() {
    this.publicReferralCoupons = [];
    this.allReferralUsages = [];
    this.referralStats = {
      totalPublicCodes: 0,
      totalUsages: 0,
      totalDiscountGiven: 0,
      activeReferrers: 0
    };
    this.isLoading = false;
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-xl font-bold text-gray-900">Referral Program Dashboard</h2>
            <p class="text-gray-600">Monitor all referral codes and usage across your customer base</p>
          </div>
          <button 
            id="refresh-referral-btn" 
            class="bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700"
          >
            Refresh
          </button>
        </div>

        <!-- How It Works Section -->
        <div class="bg-gradient-to-r from-violet-50 to-purple-50 p-6 rounded-lg mb-6 border border-violet-200">
          <h3 class="text-lg font-semibold text-violet-900 mb-4">🎉 How the Referral Program Works</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div class="space-y-2">
              <div class="flex items-start">
                <span class="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                <div>
                  <p class="font-medium text-violet-900">Complete an order to get your unique referral code</p>
                  <p class="text-violet-700">After first order completion, customer receives a personalized code</p>
                </div>
              </div>
              <div class="flex items-start">
                <span class="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                <div>
                  <p class="font-medium text-violet-900">Share code with friends</p>
                  <p class="text-violet-700">Friends get 10% off their first order when they use the code</p>
                </div>
              </div>
            </div>
            <div class="space-y-2">
              <div class="flex items-start">
                <span class="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                <div>
                  <p class="font-medium text-violet-900">Earn rewards for each successful referral</p>
                  <p class="text-violet-700">Referrer gets 10% off added to their reward balance (up to 100%)</p>
                </div>
              </div>
              <div class="flex items-start">
                <span class="bg-violet-100 text-violet-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                <div>
                  <p class="font-medium text-violet-900">SMS notification system</p>
                  <p class="text-violet-700">Referrers get SMS notifications when their code is used</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Stats Overview -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="bg-violet-50 p-4 rounded-lg border border-violet-200">
            <div class="flex items-center">
              <div class="p-2 bg-violet-100 rounded-lg">
                <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-violet-600">Public Referral Codes</p>
                <p class="text-2xl font-semibold text-violet-900" id="total-public-codes-stat">-</p>
              </div>
            </div>
          </div>

          <div class="bg-green-50 p-4 rounded-lg border border-green-200">
            <div class="flex items-center">
              <div class="p-2 bg-green-100 rounded-lg">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-green-600">Total Referral Uses</p>
                <p class="text-2xl font-semibold text-green-900" id="total-uses-stat">-</p>
              </div>
            </div>
          </div>

          <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div class="flex items-center">
              <div class="p-2 bg-blue-100 rounded-lg">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-blue-600">Total Discount Given</p>
                <p class="text-2xl font-semibold text-blue-900" id="total-discount-given-stat">-</p>
              </div>
            </div>
          </div>

          <div class="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div class="flex items-center">
              <div class="p-2 bg-orange-100 rounded-lg">
                <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-orange-600">Active Referrers</p>
                <p class="text-2xl font-semibold text-orange-900" id="active-referrers-stat">-</p>
              </div>
            </div>
          </div>
        </div>

        <!-- All Public Referral Codes -->
        <div class="mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">All Public Referral Codes</h3>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 border border-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referral Code
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referrer
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount Value
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody id="public-referral-codes-tbody" class="bg-white divide-y divide-gray-200">
                <tr>
                  <td colspan="6" class="px-6 py-4 text-center text-gray-500">
                    <div class="animate-pulse">Loading referral codes...</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- All Referral Usage History -->
        <div class="mb-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">All Referral Usage History</h3>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 border border-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Used
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referral Code
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referrer
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    New Customer
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Number
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount Given
                  </th>
                </tr>
              </thead>
              <tbody id="referral-usage-history-tbody" class="bg-white divide-y divide-gray-200">
                <tr>
                  <td colspan="6" class="px-6 py-4 text-center text-gray-500">
                    <div class="animate-pulse">Loading usage history...</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Notification Area -->
        <div id="referral-notification" class="hidden p-4 rounded-md">
          <div id="referral-notification-content"></div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    await this.loadReferralData();

    // Refresh button
    document.getElementById('refresh-referral-btn').addEventListener('click', () => {
      this.loadReferralData();
    });
  }

  async loadReferralData() {
    this.isLoading = true;
    this.showLoadingState();

    try {
      // Get all public referral coupons with referrer details
      const { data: publicCoupons, error: couponsError } = await supabase
        .from('coupons')
        .select(`
          *,
          referrer_customer:referred_by_customer_id (
            full_name,
            email
          )
        `)
        .eq('is_public_referral_code', true)
        .order('created_at', { ascending: false });

      if (couponsError) throw couponsError;

      this.publicReferralCoupons = publicCoupons || [];

      // Get all referral usages with complete details
      const { data: allUsages, error: usagesError } = await supabase
        .from('referral_usages')
        .select(`
          *,
          referral_coupon:referral_coupon_id (
            code,
            referred_by_customer_id (
              full_name,
              email
            )
          ),
          referred_customer:referred_customer_id (
            full_name,
            email
          ),
          referred_order:referred_order_id (
            order_number
          )
        `)
        .order('used_at', { ascending: false });

      if (usagesError) throw usagesError;

      this.allReferralUsages = allUsages || [];

      // Calculate stats
      this.calculateStats();
      this.renderBusinessDashboard();

    } catch (error) {
      this.showError('Error loading referral data: ' + error.message);
    } finally {
      this.isLoading = false;
    }
  }

  calculateStats() {
    this.referralStats = {
      totalPublicCodes: this.publicReferralCoupons.length,
      totalUsages: this.allReferralUsages.length,
      totalDiscountGiven: this.allReferralUsages.reduce((sum, usage) => sum + usage.earned_discount_percentage, 0),
      activeReferrers: this.publicReferralCoupons.filter(coupon => coupon.is_active).length
    };
  }

  showLoadingState() {
    // Stats already show "-" which indicates loading
    // Tables already show "Loading..." messages
  }

  renderBusinessDashboard() {
    // Update stats
    document.getElementById('total-public-codes-stat').textContent = this.referralStats.totalPublicCodes;
    document.getElementById('total-uses-stat').textContent = this.referralStats.totalUsages;
    document.getElementById('total-discount-given-stat').textContent = `${this.referralStats.totalDiscountGiven}%`;
    document.getElementById('active-referrers-stat').textContent = this.referralStats.activeReferrers;

    // Render tables
    this.renderPublicReferralCodesTable();
    this.renderAllReferralUsageHistoryTable();
  }

  renderPublicReferralCodesTable() {
    const tbody = document.getElementById('public-referral-codes-tbody');

    if (this.publicReferralCoupons.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="6" class="px-6 py-4 text-center text-gray-500">
            No public referral codes found. Codes are generated when customers complete their first order.
          </td>
        </tr>
      `;
      return;
    }

    tbody.innerHTML = this.publicReferralCoupons.map(coupon => {
      const statusColor = coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
      const statusText = coupon.is_active ? 'Active' : 'Inactive';
      
      const usageDisplay = coupon.usage_limit 
        ? `${coupon.used_count || 0} / ${coupon.usage_limit}`
        : `${coupon.used_count || 0} uses`;

      return `
        <tr class="hover:bg-gray-50">
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="font-mono font-bold text-violet-600">${coupon.code}</div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm font-medium text-gray-900">${coupon.referrer_customer?.full_name || 'Unknown'}</div>
            <div class="text-sm text-gray-500">${coupon.referrer_customer?.email || 'No email'}</div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm font-medium text-gray-900">${coupon.value}% off</div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm text-gray-900">${usageDisplay}</div>
            ${coupon.usage_limit && coupon.used_count >= coupon.usage_limit ? 
              '<div class="text-xs text-red-500">Limit reached</div>' : ''}
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColor}">
              ${statusText}
            </span>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            ${new Date(coupon.created_at).toLocaleDateString()}
          </td>
        </tr>
      `;
    }).join('');
  }

  renderAllReferralUsageHistoryTable() {
    const tbody = document.getElementById('referral-usage-history-tbody');

    if (this.allReferralUsages.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="6" class="px-6 py-4 text-center text-gray-500">
            No referral usage history found. Usage will appear here when customers use referral codes.
          </td>
        </tr>
      `;
      return;
    }

    tbody.innerHTML = this.allReferralUsages.map(usage => `
      <tr class="hover:bg-gray-50">
        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          ${new Date(usage.used_at).toLocaleDateString()}
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="font-mono font-medium text-violet-600">${usage.referral_coupon?.code || 'Unknown'}</div>
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="text-sm font-medium text-gray-900">${usage.referral_coupon?.referred_by_customer_id?.full_name || 'Unknown'}</div>
          <div class="text-sm text-gray-500">${usage.referral_coupon?.referred_by_customer_id?.email || 'No email'}</div>
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <div class="text-sm font-medium text-gray-900">${usage.referred_customer?.full_name || 'Unknown'}</div>
          <div class="text-sm text-gray-500">${usage.referred_customer?.email || 'No email'}</div>
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          ${usage.referred_order?.order_number || 'Unknown'}
        </td>
        <td class="px-6 py-4 whitespace-nowrap">
          <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            ${usage.earned_discount_percentage}% discount
          </span>
        </td>
      </tr>
    `).join('');
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showNotification(message, type = 'info') {
    const notificationDiv = document.getElementById('referral-notification');
    const contentDiv = document.getElementById('referral-notification-content');
    
    const colorClasses = {
      'info': 'bg-violet-50 text-violet-800 border border-violet-200',
      'success': 'bg-green-50 text-green-800 border border-green-200',
      'warning': 'bg-yellow-50 text-yellow-800 border border-yellow-200',
      'error': 'bg-red-50 text-red-800 border border-red-200'
    };

    notificationDiv.className = `p-4 rounded-md ${colorClasses[type] || colorClasses.info}`;
    contentDiv.innerHTML = `
      <div class="flex items-center">
        <div class="flex-1">${message}</div>
        <button 
          onclick="document.getElementById('referral-notification').classList.add('hidden')" 
          class="ml-4 text-current hover:opacity-75"
        >
          ✕
        </button>
      </div>
    `;
    
    notificationDiv.classList.remove('hidden');

    // Auto-hide success and info messages after 5 seconds
    if (type === 'success' || type === 'info') {
      setTimeout(() => {
        notificationDiv.classList.add('hidden');
      }, 5000);
    }
  }
}